import type {
  SECTION_WISE_SAVED_FIELD_MAPPING,
  SETTINGS_OF_PLATFORM,
  ShippingMethodMapping,
} from 'src/interfaces/Platform.interface'

import {getDirectSettingFileChildActions, isSettingFileChildInputType} from './settingFileFieldMappings'

type FieldMappingSaveEntry = Record<string, any>

export const isSavedFieldMappingsPayload = (payload: unknown): boolean => {
  if (Array.isArray(payload)) return true
  if (!payload || typeof payload !== 'object') return false
  return Array.isArray((payload as {data?: unknown}).data)
}

export const normalizeSavedFieldMappingsPayload = (
  payload: unknown
): SECTION_WISE_SAVED_FIELD_MAPPING[] => {
  if (Array.isArray(payload)) {
    return payload as SECTION_WISE_SAVED_FIELD_MAPPING[]
  }

  if (payload && typeof payload === 'object') {
    const data = (payload as {data?: unknown}).data
    if (Array.isArray(data)) {
      return data as SECTION_WISE_SAVED_FIELD_MAPPING[]
    }
  }

  return []
}

export const ORDER_TYPE_GROUP_CODES = [
  'ORDER',
  'ACCOUNTING_ORDER',
  'ERP_ORDER',
  'OMS_ORDER',
  'BACK_SHIPMENT',
] as const

export type OrderTypeGroupCode = (typeof ORDER_TYPE_GROUP_CODES)[number]

export const isOrderTypeGroupCode = (groupCode?: string | null): boolean =>
  ORDER_TYPE_GROUP_CODES.includes(groupCode as OrderTypeGroupCode)

export const resolveSecondaryOrderTypeDependentLookupCodes = (
  groupCode: string | undefined,
  fieldMapping: FieldMappingSaveEntry | null | undefined,
  mappedWith: FieldMappingSaveEntry | null | undefined
) => {
  if (isOrderTypeGroupCode(groupCode)) {
    return {
      sourceCode: fieldMapping?.api_code ?? fieldMapping?.field_code ?? null,
      destinationCode: mappedWith?.api_code ?? mappedWith?.field_code ?? null,
    }
  }

  return {
    sourceCode: mappedWith?.api_code ?? mappedWith?.field_code ?? null,
    destinationCode: fieldMapping?.api_code ?? fieldMapping?.field_code ?? null,
  }
}

export const buildFieldMappingDedupeKey = (entry: FieldMappingSaveEntry): string => {
  const sideIds = [entry.sourceFieldId, entry.destinationFieldId]
    .filter((id) => id !== null && id !== undefined && id !== '')
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id))
    .sort((a, b) => a - b)
    .join(':')

  return [
    entry.groupCode ?? '',
    entry.savedSettingId ?? '',
    entry.savedMappingId ?? entry.field_mapping_id ?? '',
    entry.setting_mapping_id ?? '',
    entry.fieldCode ?? '',
    entry.isDefault ? '1' : '0',
    sideIds,
  ].join('|')
}

export const buildFieldMappingRowDedupeKey = (
  row: FieldMappingSaveEntry,
  rowIndex: number
): string => {
  const sourceKey =
    row?.source_platform_data?.id ??
    row?.source_platform_data_id ??
    row?.source_platform_data_str ??
    ''
  const destKey =
    row?.destination_platform_data?.id ??
    row?.destination_platform_data_id ??
    row?.destination_platform_data_str ??
    ''

  if (row?.id !== null && row?.id !== undefined) {
    // Default MULTISELECT rows can share the same persisted row id with different
    // destination values — dedupe by id alone drops valid selections on save.
    if (destKey !== '' || sourceKey !== '') {
      return `id:${row.id}:dest:${destKey}:src:${sourceKey}`
    }
    return `id:${row.id}`
  }

  if (sourceKey === '' && destKey === '') {
    return `draft-empty:${rowIndex}`
  }

  return `draft-pair:${sourceKey}:${destKey}`
}

export const dedupeFieldMappingDataSet = (rows: FieldMappingSaveEntry[] = []): FieldMappingSaveEntry[] => {
  const rowMap = new Map<string, FieldMappingSaveEntry>()

  rows.forEach((row, rowIndex) => {
    if (!row) return

    const key = buildFieldMappingRowDedupeKey(row, rowIndex)

    const existing = rowMap.get(key)
    if (!existing) {
      rowMap.set(key, row)
      return
    }

    if ((existing.id === null || existing.id === undefined) && row.id !== null && row.id !== undefined) {
      rowMap.set(key, row)
    }
  })

  return Array.from(rowMap.values())
}

export const resolveCanonicalPairFieldIds = (
  entry: FieldMappingSaveEntry,
  savedMappings: FieldMappingSaveEntry[] = []
) => {
  const entrySourceFieldId = entry.sourceFieldId ?? null
  const entryDestinationFieldId = entry.destinationFieldId ?? null

  const saved = (savedMappings || []).find((mapping) => {
    if (entry.savedMappingId && mapping.id === entry.savedMappingId) {
      return true
    }

    if (entryDestinationFieldId !== null && entryDestinationFieldId !== undefined) {
      const destinationMatches = mapping?.destination_field?.id === entryDestinationFieldId
      const sourceMatches =
        entrySourceFieldId === null || entrySourceFieldId === undefined
          ? mapping?.source_field == null
          : mapping?.source_field?.id === entrySourceFieldId

      if (destinationMatches && sourceMatches) {
        return true
      }
    }

    if (
      entry.setting_mapping_id !== null &&
      entry.setting_mapping_id !== undefined &&
      mapping.setting_mapping_id === entry.setting_mapping_id &&
      entry.savedSettingId !== null &&
      entry.savedSettingId !== undefined &&
      mapping.setting_id === entry.savedSettingId
    ) {
      return true
    }

    return false
  })

  if (saved?.source_field?.id != null && saved?.destination_field?.id != null) {
    return {
      sourceFieldId: saved.source_field.id,
      destinationFieldId: saved.destination_field.id,
    }
  }

  return {
    sourceFieldId: entry.sourceFieldId ?? null,
    destinationFieldId: entry.destinationFieldId ?? null,
  }
}

export const dedupeFieldMappingsForSave = (
  fieldMappings: FieldMappingSaveEntry[] = [],
  savedMappings: FieldMappingSaveEntry[] = []
): FieldMappingSaveEntry[] => {
  const mergedByKey = new Map<string, FieldMappingSaveEntry>()

  fieldMappings.forEach((entry) => {
    const key = buildFieldMappingDedupeKey(entry)
    const existing = mergedByKey.get(key)

    if (!existing) {
      mergedByKey.set(key, {
        ...entry,
        fieldMappingDataSet: [...(entry.fieldMappingDataSet || [])],
      })
      return
    }

    const combinedRows = [...(existing.fieldMappingDataSet || []), ...(entry.fieldMappingDataSet || [])]
    const mergedEntry = {
      ...existing,
      ...entry,
      field_mapping_id: existing.field_mapping_id ?? entry.field_mapping_id,
      savedMappingId: existing.savedMappingId ?? entry.savedMappingId,
      fieldMappingDataSet: dedupeFieldMappingDataSet(combinedRows),
    }

    mergedByKey.set(key, {
      ...mergedEntry,
      ...resolveCanonicalPairFieldIds(mergedEntry, savedMappings),
    })
  })

  return Array.from(mergedByKey.values()).map((entry) => ({
    ...entry,
    ...resolveCanonicalPairFieldIds(entry, savedMappings),
    fieldMappingDataSet: dedupeFieldMappingDataSet(entry.fieldMappingDataSet || []),
  }))
}

export const resolveSavedFieldMappingIsDefault = (
  savedItem: FieldMappingSaveEntry
): boolean | undefined => {
  if (savedItem?.isDefault !== undefined && savedItem?.isDefault !== null) {
    return Boolean(savedItem.isDefault)
  }

  if (
    savedItem?.destination_field?.isDefault !== undefined &&
    savedItem?.destination_field?.isDefault !== null
  ) {
    return Boolean(savedItem.destination_field.isDefault)
  }

  return undefined
}

export const isLocationFieldMappingCode = (mapping: FieldMappingSaveEntry): boolean => {
  const code =
    mapping?.api_code ??
    mapping?.field_code ??
    mapping?.destination_field?.api_code ??
    mapping?.destination_field?.field_code

  return `${code ?? ''}`.trim().toUpperCase() === 'LOCATION'
}

/** Normalize composite store setting types (e.g. SHIPPING_SECONDARY_STORE_SETTING). */
export const normalizeStoreSettingType = (settingType?: string | null): string =>
  `${settingType ?? ''}`.replace('_SECONDARY', '')

export const resolvePlatformSettingsByType = <T>(
  settingType: string | undefined | null,
  settingsByType: Record<string, T>
): T | [] => {
  if (!settingType) {
    return []
  }

  if (Object.prototype.hasOwnProperty.call(settingsByType, settingType)) {
    return settingsByType[settingType] ?? []
  }

  const normalizedType = normalizeStoreSettingType(settingType)
  if (
    normalizedType !== settingType &&
    Object.prototype.hasOwnProperty.call(settingsByType, normalizedType)
  ) {
    return settingsByType[normalizedType] ?? []
  }

  return []
}

export type SelectableSourcePlatformRef = {
  id?: number | null
  setting_type?: string | null
  available_platform_id?: number | null
}

export type GroupScopedSourceSettingsState<T = unknown> = {
  settings?: T
  sourcePlatformId?: number | null
  settingType?: string | null
}

/** Reuse a type bucket only when it was loaded for this exact platform. */
export const pickLoadedSettingsForSource = <T extends unknown[]>(
  source: SelectableSourcePlatformRef | null | undefined,
  storeIdByType: Record<string, number | null | undefined>,
  settingsByType: Record<string, T>
): T | null => {
  const settingType = source?.setting_type
  if (!settingType || source?.id == null) {
    return null
  }

  if (Number(storeIdByType[settingType]) !== Number(source.id)) {
    return null
  }

  const loaded = resolvePlatformSettingsByType(settingType, settingsByType)
  return Array.isArray(loaded) && loaded.length ? (loaded as T) : null
}

/**
 * Prefer settings fetched for this section's selected platform.
 * Do not fall back to the global type bucket when that bucket belongs to a different platform.
 */
export const resolveGroupScopedPlatformSettings = <T extends unknown[]>(
  groupState: GroupScopedSourceSettingsState<T> | null | undefined,
  settingType: string | undefined | null,
  settingsByType: Record<string, T>,
  loadedStoreIdForType?: number | null
): T | [] => {
  const groupSettings = groupState?.settings
  if (Array.isArray(groupSettings)) {
    return groupSettings
  }

  if (
    groupState?.sourcePlatformId != null &&
    loadedStoreIdForType != null &&
    Number(groupState.sourcePlatformId) !== Number(loadedStoreIdForType)
  ) {
    return []
  }

  return resolvePlatformSettingsByType(settingType, settingsByType)
}

/** Source store types where default LOCATION is hidden when a one-to-one LOCATION slot exists. */
export const DEFAULT_LOCATION_HIDE_SOURCE_SETTING_TYPES = new Set([
  'MAIN_STORE_SETTING',
  'ACCOUNTING_STORE_SETTING',
  'SHIPPING_STORE_SETTING',
  'OMS_STORE_SETTING',
  'ERP_STORE_SETTING',
  'SECONDARY_STORE_SETTING',
  'TICKET_STORE_SETTING',
])

const mappingFieldCodeKey = (mapping: FieldMappingSaveEntry | null | undefined): string =>
  `${mapping?.api_code ?? mapping?.field_code ?? ''}`.trim().toUpperCase()

const relatedAvailablePlatformValue = (
  mapping: FieldMappingSaveEntry | null | undefined
): string | null => {
  const related = mapping?.related_available_platform
  if (related === null || related === undefined || related === '') {
    return null
  }
  return `${related}`
}

/**
 * When a field code has a channel-specific catalog slot for the current available platform,
 * keep only those slots and drop generic (`related_available_platform: null`) duplicates.
 * Uses `field_mappings` meta joined by `setting_mapping_id` because `mappings_data` often omits related.
 */
export const filterMappingsByRelatedAvailablePlatform = (
  mappingsData: FieldMappingSaveEntry[] = [],
  fieldMappingsMeta: FieldMappingSaveEntry[] = [],
  relatedAvailablePlatformId?: number | string | null
): FieldMappingSaveEntry[] => {
  if (!Array.isArray(mappingsData) || mappingsData.length === 0) {
    return mappingsData
  }

  if (
    relatedAvailablePlatformId === null ||
    relatedAvailablePlatformId === undefined ||
    relatedAvailablePlatformId === ''
  ) {
    return mappingsData
  }

  const relatedKey = `${relatedAvailablePlatformId}`
  const metaBySettingMappingId = new Map<string, FieldMappingSaveEntry>()
  ;(fieldMappingsMeta ?? []).forEach((meta) => {
    if (meta?.id === null || meta?.id === undefined) {
      return
    }
    metaBySettingMappingId.set(`${meta.id}`, meta)
  })

  const resolveRelated = (row: FieldMappingSaveEntry): string | null => {
    const meta = metaBySettingMappingId.get(`${row?.setting_mapping_id ?? ''}`)
    return relatedAvailablePlatformValue(meta) ?? relatedAvailablePlatformValue(row)
  }

  const codesWithChannelSpecific = new Set<string>()
  mappingsData.forEach((row) => {
    const code = mappingFieldCodeKey(row)
    if (!code) {
      return
    }
    if (resolveRelated(row) === relatedKey) {
      codesWithChannelSpecific.add(code)
    }
  })

  if (!codesWithChannelSpecific.size) {
    return mappingsData
  }

  return mappingsData.filter((row) => {
    const code = mappingFieldCodeKey(row)
    if (!codesWithChannelSpecific.has(code)) {
      return true
    }
    return resolveRelated(row) === relatedKey
  })
}

// True if mappings list has a Location pair slot; in: mapping rows; out: true when any LOCATION has isDefault === false.
export const hasNonDefaultLocationMapping = (
  mappings: FieldMappingSaveEntry[] = []
): boolean =>
  mappings.some(
    (mapping) =>
      Boolean(mapping) &&
      mapping?.isDefault === false &&
      isLocationFieldMappingCode(mapping)
  )

/** True when the setting group defines a one-to-one LOCATION mapping slot. */
export const hasOneToOneLocationInGroup = (
  mappings: FieldMappingSaveEntry[] = []
): boolean => hasNonDefaultLocationMapping(mappings)

// Hide default Location Independent Mapping only when this group and the other side both have a pair slot; in: row, settingType, opposite mappings_data, this group's mappings_data; out: true to skip Independent Mapping.
export const shouldHideDefaultLocationMapping = (
  mapping: FieldMappingSaveEntry,
  settingType: string | undefined,
  oppositeMappings: FieldMappingSaveEntry[] = [],
  owningMappings: FieldMappingSaveEntry[] = []
): boolean => {
  const normalizedSettingType = normalizeStoreSettingType(settingType)

  return Boolean(
    normalizedSettingType &&
      DEFAULT_LOCATION_HIDE_SOURCE_SETTING_TYPES.has(normalizedSettingType) &&
      mapping?.isDefault &&
      isLocationFieldMappingCode(mapping) &&
      hasNonDefaultLocationMapping(oppositeMappings) &&
      hasNonDefaultLocationMapping(owningMappings)
  )
}

// ─── Generic: Hide default when one-to-one data exists ──────────────────────

/** Returns true when the mapping's field_code / api_code matches the given target code. */
export const matchesFieldCode = (
  mapping: FieldMappingSaveEntry | null | undefined,
  targetCode: string
): boolean => {
  if (!mapping) return false
  const code =
    mapping?.api_code ??
    mapping?.field_code ??
    mapping?.destination_field?.api_code ??
    mapping?.destination_field?.field_code
  return `${code ?? ''}`.trim().toUpperCase() === targetCode.trim().toUpperCase()
}

/**
 * Returns true when there is at least one non-default, non-empty saved or pending
 * one-to-one mapping row for the given field code and setting.
 */
export const hasActiveOneToOneMapping = ({
  savedMappings = [],
  pendingMappings = [],
  settingId,
  targetCode,
}: {
  savedMappings?: FieldMappingSaveEntry[]
  pendingMappings?: FieldMappingSaveEntry[]
  settingId?: number | string | null
  targetCode: string
}): boolean => {
  const hasSavedRow = savedMappings.some((item) => {
    if (!item || item.isDefault !== false) return false
    if (!matchesFieldCode(item, targetCode) && !matchesFieldCode(item?.destination_field, targetCode)) {
      return false
    }
    if (settingId !== null && settingId !== undefined) {
      const savedSettingId = item?.setting_id
      if (savedSettingId !== null && savedSettingId !== undefined) {
        if (Number(savedSettingId) !== Number(settingId)) return false
      }
    }
    return hasMappingDatasetValue(item?.fieldmappingdata_set ?? [], item)
  })

  if (hasSavedRow) return true

  return pendingMappings.some((entry) => {
    if (!entry || entry.isDefault !== false) return false
    if (!matchesFieldCode(entry, targetCode)) return false
    if (settingId !== null && settingId !== undefined) {
      const entrySavedSettingId = entry?.savedSettingId
      if (entrySavedSettingId !== null && entrySavedSettingId !== undefined) {
        if (Number(entrySavedSettingId) !== Number(settingId)) return false
      }
    }
    return hasMappingDatasetValue(entry?.fieldMappingDataSet ?? [], entry)
  })
}

/**
 * Generic rule: hide the default slot for a given field code when
 * an active one-to-one row already exists (saved or pending).
 */
export const shouldHideDefaultWhenOneToOneExists = ({
  mapping,
  savedMappings = [],
  pendingMappings = [],
  settingId,
  targetCode,
}: {
  mapping: FieldMappingSaveEntry
  savedMappings?: FieldMappingSaveEntry[]
  pendingMappings?: FieldMappingSaveEntry[]
  settingId?: number | string | null
  targetCode: string
}): boolean => {
  if (!mapping?.isDefault) return false
  if (!matchesFieldCode(mapping, targetCode)) return false

  return hasActiveOneToOneMapping({ savedMappings, pendingMappings, settingId, targetCode })
}


/** Skip rendering source-side non-default LOCATION; the pair is owned by the opposite channel or same-side dual slot. */
export const shouldSourceLocationOwnPair = (
  sourceMapping: FieldMappingSaveEntry,
  oppositeMappings: FieldMappingSaveEntry[] = [],
  sourceSideMappings: FieldMappingSaveEntry[] = []
): boolean =>
  Boolean(
    isLocationFieldMappingCode(sourceMapping) &&
      !sourceMapping?.isDefault &&
      (hasNonDefaultLocationMapping(oppositeMappings) ||
        hasNonDefaultLocationMapping(sourceSideMappings))
  )

/** @deprecated Use shouldSourceLocationOwnPair */
export const shouldSelectedInventoryLocationOwnPair = shouldSourceLocationOwnPair

type PlatformSettingGroupEntry = {
  group_code?: string
  mappings_data?: FieldMappingSaveEntry[]
}

export const findSourceLocationMapping = (
  sourceSettings: PlatformSettingGroupEntry[] = [],
  groupCode?: string,
  destinationMapping?: FieldMappingSaveEntry
): FieldMappingSaveEntry | null => {
  if (!destinationMapping || !isLocationFieldMappingCode(destinationMapping)) {
    return null
  }

  const sameGroupSetting = sourceSettings?.find((item) => item?.group_code === groupCode)
  const sameGroupLocation = sameGroupSetting?.mappings_data?.find((mapping) =>
    isLocationFieldMappingCode(mapping)
  )
  if (sameGroupLocation) {
    return sameGroupLocation
  }

  return (
    sourceSettings
      ?.flatMap((item) => item?.mappings_data ?? [])
      ?.find((mapping) => isLocationFieldMappingCode(mapping)) ?? null
  )
}

export const findSavedIndependentLocationMapping = (
  savedMappings: FieldMappingSaveEntry[] = [],
  options?: {
    destinationPlatformId?: number | string | null
    settingId?: number | string | null
  }
) =>
  savedMappings.find((item) => {
    if (!isLocationFieldMappingCode(item) && !isLocationFieldMappingCode(item?.destination_field)) {
      return false
    }

    if (item?.source_field != null) {
      return false
    }

    const {destinationPlatformId, settingId} = options ?? {}

    if (settingId !== null && settingId !== undefined) {
      const savedSettingId = item?.setting_id
      if (savedSettingId !== null && savedSettingId !== undefined) {
        if (Number(savedSettingId) !== Number(settingId)) {
          return false
        }
      }
    }

    if (destinationPlatformId !== null && destinationPlatformId !== undefined) {
      const savedPlatform = item?.destination_field?.platform
      if (
        savedPlatform !== null &&
        savedPlatform !== undefined &&
        Number(savedPlatform) !== Number(destinationPlatformId)
      ) {
        return false
      }
    }

    return true
  })

/** Opposite/source channel exposes a one-to-one LOCATION pair slot. */
export const oppositeChannelHasOneToOneLocationPair = (
  oppositeMappings: FieldMappingSaveEntry[] = []
): boolean => hasNonDefaultLocationMapping(oppositeMappings)

/** Hide the one-to-one LOCATION slot when the opposite channel has no pair to map with. */
export const shouldHideOneToOneLocationSlot = (
  fieldMapping: FieldMappingSaveEntry,
  oppositeMappings: FieldMappingSaveEntry[] = [],
  mappingsData: FieldMappingSaveEntry[] = []
): boolean =>
  Boolean(
    isLocationFieldMappingCode(fieldMapping) &&
      !fieldMapping?.isDefault &&
      hasDualIndependentLocationSlots(mappingsData) &&
      !oppositeChannelHasOneToOneLocationPair(oppositeMappings)
  )

/** When a setting group defines default + non-default LOCATION slots, pick the slot that matches persisted data. */
export const resolveIndependentLocationFieldMappingSlot = (
  currentSlot: FieldMappingSaveEntry,
  savedMappings: FieldMappingSaveEntry[] = [],
  options?: {
    destinationPlatformId?: number | string | null
    settingId?: number | string | null
    locationSlotCandidates?: FieldMappingSaveEntry[]
    oppositeMappings?: FieldMappingSaveEntry[]
  }
): FieldMappingSaveEntry => {
  const {locationSlotCandidates = [], oppositeMappings = []} = options ?? {}

  if (!oppositeChannelHasOneToOneLocationPair(oppositeMappings)) {
    const defaultSlot = locationSlotCandidates.find((slot) => slot.isDefault === true)
    if (defaultSlot) {
      return defaultSlot
    }
  }

  const saved = findSavedIndependentLocationMapping(savedMappings, options)
  if (!saved) {
    return currentSlot
  }

  const savedSettingMappingId = saved.setting_mapping_id

  if (savedSettingMappingId !== null && savedSettingMappingId !== undefined) {
    const matchedSlot = locationSlotCandidates.find(
      (slot) => slot.setting_mapping_id === savedSettingMappingId
    )
    if (matchedSlot) {
      return matchedSlot
    }
  }

  const savedIsDefault = resolveSavedFieldMappingIsDefault(saved)
  if (savedIsDefault === true) {
    const defaultSlot = locationSlotCandidates.find((slot) => slot.isDefault === true)
    if (defaultSlot) {
      return defaultSlot
    }
  }

  if (savedIsDefault === false) {
    const nonDefaultSlot = locationSlotCandidates.find((slot) => slot.isDefault === false)
    if (nonDefaultSlot) {
      return nonDefaultSlot
    }
  }

  return currentSlot
}

/** @deprecated Use resolveIndependentLocationFieldMappingSlot */
export const resolveInventoryLocationFieldMappingSlot = resolveIndependentLocationFieldMappingSlot

export const collectLocationSlotCandidates = (
  mappingsData: FieldMappingSaveEntry[] | null | undefined = []
): FieldMappingSaveEntry[] => (mappingsData ?? []).filter(isLocationFieldMappingCode)

export const hasDualIndependentLocationSlots = (
  mappingsData: FieldMappingSaveEntry[] = []
): boolean => {
  const locationSlots = collectLocationSlotCandidates(mappingsData)
  return (
    locationSlots.some((slot) => slot.isDefault === true) &&
    locationSlots.some((slot) => slot.isDefault === false)
  )
}

export const shouldResolveIndependentLocationFieldMappingSlot = (
  fieldMapping: FieldMappingSaveEntry,
  isMappedWith: unknown,
  mappingsData: FieldMappingSaveEntry[] = []
): boolean =>
  isLocationFieldMappingCode(fieldMapping) &&
  !isMappedWith &&
  hasDualIndependentLocationSlots(mappingsData)

export const matchesIndependentFieldMappingSlot = (
  savedItem: FieldMappingSaveEntry,
  fieldMapping: FieldMappingSaveEntry,
  options?: {
    destinationPlatformId?: number | string | null
    settingId?: number | string | null
  }
): boolean => {
  if (!savedItem || !fieldMapping) {
    return false
  }

  const {destinationPlatformId, settingId} = options ?? {}

  if (settingId !== null && settingId !== undefined) {
    const savedSettingId = savedItem?.setting_id
    if (savedSettingId !== null && savedSettingId !== undefined) {
      if (Number(savedSettingId) !== Number(settingId)) {
        return false
      }
    }
  }

  const slotSettingMappingId = fieldMapping?.setting_mapping_id
  const savedSettingMappingId = savedItem?.setting_mapping_id
  if (
    slotSettingMappingId !== null &&
    slotSettingMappingId !== undefined &&
    savedSettingMappingId !== null &&
    savedSettingMappingId !== undefined
  ) {
    return savedSettingMappingId === slotSettingMappingId
  }

  const savedDestCode =
    savedItem?.destination_field?.api_code ?? savedItem?.destination_field?.field_code
  const slotCode = fieldMapping?.api_code ?? fieldMapping?.field_code
  if (savedDestCode !== slotCode) {
    return false
  }

  if (savedItem?.source_field != null) {
    return false
  }

  if (destinationPlatformId !== null && destinationPlatformId !== undefined) {
    const savedPlatform = savedItem?.destination_field?.platform
    if (
      savedPlatform !== null &&
      savedPlatform !== undefined &&
      Number(savedPlatform) !== Number(destinationPlatformId)
    ) {
      return false
    }
  }

  const slotIsDefault = fieldMapping?.isDefault
  const savedIsDefault = resolveSavedFieldMappingIsDefault(savedItem)
  if (slotIsDefault !== undefined && savedIsDefault !== undefined) {
    return Boolean(slotIsDefault) === Boolean(savedIsDefault)
  }

  if (slotSettingMappingId !== null && slotSettingMappingId !== undefined) {
    return false
  }

  return true
}

export const findIndependentSavedFieldMapping = (
  savedMappings: FieldMappingSaveEntry[] = [],
  fieldMapping: FieldMappingSaveEntry,
  options?: {
    destinationPlatformId?: number | string | null
    settingId?: number | string | null
  }
) => {
  const found = savedMappings.find((item) =>
    matchesIndependentFieldMappingSlot(item, fieldMapping, options)
  )
  return found ?? null
}

export type DependentSavedFieldMappingLookup = {
  sourceSide?: FieldMappingSaveEntry | null
  destinationSide?: FieldMappingSaveEntry | null
  effectiveSettingMappingId?: number | string | null
  settingId?: number | string | null
  isDefault?: boolean | null
  sourceCode?: string | null
  destinationCode?: string | null
  sourcePlatformId?: number | string | null
  destinationPlatformId?: number | string | null
}

const savedRowSourcePlatformMatches = (
  savedItem: FieldMappingSaveEntry,
  platformId?: number | string | null
): boolean => {
  if (platformId === null || platformId === undefined || platformId === '') {
    return false
  }

  return (savedItem.fieldmappingdata_set ?? []).some(
    (row) =>
      row?.source_platform_data?.platform !== null &&
      row?.source_platform_data?.platform !== undefined &&
      Number(row.source_platform_data.platform) === Number(platformId)
  )
}

export const matchesDependentFieldMappingSlot = (
  savedItem: FieldMappingSaveEntry,
  lookup: DependentSavedFieldMappingLookup
): boolean => {
  if (!savedItem) {
    return false
  }

  const {
    sourceSide,
    destinationSide,
    effectiveSettingMappingId,
    settingId,
    isDefault,
    sourceCode,
    destinationCode,
    sourcePlatformId,
    destinationPlatformId,
  } = lookup

  if (settingId !== null && settingId !== undefined) {
    const savedSettingId = savedItem.setting_id
    if (savedSettingId !== null && savedSettingId !== undefined) {
      if (Number(savedSettingId) !== Number(settingId)) {
        return false
      }
    }
  }

  if (
    effectiveSettingMappingId !== null &&
    effectiveSettingMappingId !== undefined &&
    savedItem.setting_mapping_id !== null &&
    savedItem.setting_mapping_id !== undefined &&
    Number(savedItem.setting_mapping_id) === Number(effectiveSettingMappingId)
  ) {
    if (isDefault !== null && isDefault !== undefined && savedItem.isDefault !== undefined) {
      if (Boolean(savedItem.isDefault) !== Boolean(isDefault)) {
        return false
      }
    }
    return true
  }

  const destinationApiCode =
    destinationCode ?? destinationSide?.api_code ?? destinationSide?.field_code ?? null
  const sourceApiCode = sourceCode ?? sourceSide?.api_code ?? sourceSide?.field_code ?? null
  const resolvedDestinationPlatformId =
    destinationPlatformId ??
    destinationSide?.platform ??
    savedItem?.destination_field?.platform ??
    null
  const resolvedSourcePlatformId =
    sourcePlatformId ?? sourceSide?.platform ?? savedItem?.source_field?.platform ?? null

  const destinationMatches =
    (destinationSide?.id != null && savedItem?.destination_field?.id === destinationSide.id) ||
    (destinationApiCode &&
      savedItem?.destination_field?.api_code === destinationApiCode &&
      (resolvedDestinationPlatformId == null ||
        savedItem?.destination_field?.platform == null ||
        Number(savedItem.destination_field.platform) === Number(resolvedDestinationPlatformId)))

  if (!destinationMatches) {
    return false
  }

  const sourceMatches =
    (sourceSide?.id != null && savedItem?.source_field?.id === sourceSide.id) ||
    (sourceApiCode &&
      savedItem?.source_field?.api_code === sourceApiCode &&
      (resolvedSourcePlatformId == null ||
        savedItem?.source_field?.platform == null ||
        Number(savedItem.source_field.platform) === Number(resolvedSourcePlatformId))) ||
    (sourceApiCode &&
      savedItem?.source_field?.api_code === sourceApiCode &&
      resolvedSourcePlatformId != null &&
      savedRowSourcePlatformMatches(savedItem, resolvedSourcePlatformId))

  if (!sourceMatches) {
    return false
  }

  if (isDefault !== null && isDefault !== undefined && savedItem.isDefault !== undefined) {
    if (Boolean(savedItem.isDefault) !== Boolean(isDefault)) {
      return false
    }
  }

  return true
}

const pickPreferredDependentSavedFieldMapping = (
  matches: FieldMappingSaveEntry[],
  lookup: DependentSavedFieldMappingLookup
) => {
  if (!matches.length) {
    return null
  }

  if (matches.length === 1) {
    return matches[0]
  }

  const targetSettingMappingId = lookup.effectiveSettingMappingId
  if (targetSettingMappingId !== null && targetSettingMappingId !== undefined) {
    const exactSettingMappingMatch = matches.find(
      (item) =>
        item?.setting_mapping_id !== null &&
        item?.setting_mapping_id !== undefined &&
        Number(item.setting_mapping_id) === Number(targetSettingMappingId)
    )
    if (exactSettingMappingMatch) {
      return exactSettingMappingMatch
    }
  }

  return matches.reduce((best, item) =>
    Number(item?.id ?? 0) > Number(best?.id ?? 0) ? item : best
  )
}

export const findDependentSavedFieldMapping = (
  savedMappings: FieldMappingSaveEntry[] = [],
  lookup: DependentSavedFieldMappingLookup
) => {
  const matches = savedMappings.filter((item) =>
    matchesDependentFieldMappingSlot(item, lookup)
  )
  return pickPreferredDependentSavedFieldMapping(matches, lookup)
}

const ORDER_ALIASED_CONTAINER_FIELDS = ['accounting_order', 'erp_order', 'oms_order'] as const
const PAYOUT_BACKEND_FIELD_MAPPINGS_KEY = 'payout_field_mappings'
const ORDER_PAYOUT_FRONTEND_FIELD_MAPPINGS_KEY = 'order_payout_field_mappings'

const toSettingFieldType = (settingKeyOrFieldType: string) =>
  settingKeyOrFieldType.replace(/_setting$/, '')

export const resolveSettingContainerMappingsKeys = (
  settingKeyOrFieldType: string
): string[] => {
  const fieldType = toSettingFieldType(settingKeyOrFieldType)
  const primary = (ORDER_ALIASED_CONTAINER_FIELDS as readonly string[]).includes(fieldType)
    ? 'order_field_mappings'
    : `${fieldType}_field_mappings`

  if (fieldType === 'order_payout') {
    return [primary, PAYOUT_BACKEND_FIELD_MAPPINGS_KEY]
  }

  return [primary]
}

export const collectFieldMappingIdsFromSettingContainer = (
  container: Record<string, any> | null | undefined,
  settingKeyOrFieldType: string
): number[] => {
  if (!container || typeof container !== 'object') {
    return []
  }

  const ids: number[] = []
  const seen = new Set<number>()

  resolveSettingContainerMappingsKeys(settingKeyOrFieldType).forEach((key) => {
    const rows = container[key]
    if (!Array.isArray(rows)) {
      return
    }
    rows.forEach((row) => {
      const id = Number(row?.id)
      if (!Number.isFinite(id) || seen.has(id)) {
        return
      }
      seen.add(id)
      ids.push(id)
    })
  })

  return ids
}

export const aliasBackendFieldMappingsOnSettingContainer = <T extends Record<string, any>>(
  container: T,
  settingKeyOrFieldType: string
): T => {
  if (toSettingFieldType(settingKeyOrFieldType) !== 'order_payout') {
    return container
  }

  const frontendMappings = container[ORDER_PAYOUT_FRONTEND_FIELD_MAPPINGS_KEY]
  const backendMappings = container[PAYOUT_BACKEND_FIELD_MAPPINGS_KEY]

  if (
    Array.isArray(backendMappings) &&
    backendMappings.length &&
    (!Array.isArray(frontendMappings) || frontendMappings.length === 0)
  ) {
    return {
      ...container,
      [ORDER_PAYOUT_FRONTEND_FIELD_MAPPINGS_KEY]: backendMappings,
    }
  }

  return container
}

export const mergeSavedMappingsIntoSettingValues = (
  values: Record<string, any>,
  savedMappings: FieldMappingSaveEntry[] = []
) => {
  if (!values || !savedMappings?.length) {
    return values
  }

  const next = {...values}

  Object.keys(next).forEach((settingKey) => {
    if (!settingKey.endsWith('_setting')) {
      return
    }

    const mappingsKey = resolveSettingContainerMappingsKeys(settingKey)[0]
    const rawContainer = next[settingKey]
    if (!rawContainer || typeof rawContainer !== 'object') {
      return
    }

    const container = aliasBackendFieldMappingsOnSettingContainer(rawContainer, settingKey)

    if (!Array.isArray(container[mappingsKey])) {
      return
    }

    next[settingKey] = {
      ...container,
      [mappingsKey]: container[mappingsKey].map((item: FieldMappingSaveEntry) => {
        const saved = savedMappings.find((mapping) => mapping?.id === item?.id)
        if (!saved?.fieldmappingdata_set?.length) {
          return item
        }

        return {
          ...item,
          ...saved,
          fieldmappingdata_set: saved.fieldmappingdata_set,
        }
      }),
    }
  })

  return next
}

export const resolveSettingMappingIdForSave = (
  entry: FieldMappingSaveEntry,
  savedMappings: FieldMappingSaveEntry[] = []
) => {
  if (entry.setting_mapping_id !== null && entry.setting_mapping_id !== undefined) {
    return entry.setting_mapping_id
  }

  const sourceFieldId = entry.sourceFieldId ?? null
  const destinationFieldId = entry.destinationFieldId ?? null

  if (destinationFieldId !== null && destinationFieldId !== undefined) {
    const saved = (savedMappings || []).find((mapping) => {
      if (mapping?.destination_field?.id !== destinationFieldId) {
        return false
      }

      if (sourceFieldId === null || sourceFieldId === undefined) {
        if (mapping?.source_field != null) {
          return false
        }
      } else if (mapping?.source_field?.id !== sourceFieldId) {
        return false
      }

      const entryIsDefault = entry?.isDefault
      const savedIsDefault = resolveSavedFieldMappingIsDefault(mapping)
      if (entryIsDefault !== undefined && savedIsDefault !== undefined) {
        return Boolean(entryIsDefault) === Boolean(savedIsDefault)
      }

      return true
    })

    if (saved?.setting_mapping_id !== null && saved?.setting_mapping_id !== undefined) {
      return saved.setting_mapping_id
    }
  }

  return entry.setting_mapping_id
}

type SourcePlatformSetting = {
  group_code?: string
  name?: string
  input_type?: string
  field_mappings?: unknown[] | null
  mappings_data?: unknown[] | null
  children?: SourcePlatformSetting[] | null
}

export const getSourceAdditionalChildSettings = <T extends SourcePlatformSetting>(
  selectedPlatformSettingsByGroup: T[] | null | undefined,
  groupCode: string,
  parentSettingName: string,
  secondaryChildren: T[] | null | undefined
): T[] => {
  if (!selectedPlatformSettingsByGroup?.length || !groupCode) {
    return []
  }

  const secondaryChildNames = new Set(
    (secondaryChildren ?? []).map((child) => child.name).filter(Boolean)
  )

  const sourceGroupSetting = findSourcePlatformSettingByGroup(
    selectedPlatformSettingsByGroup,
    groupCode,
    parentSettingName
  )

  const fromSameGroup = (sourceGroupSetting?.children ?? []).filter(
    (child) =>
      child?.name &&
      (!secondaryChildNames.has(child.name) || isSettingFileChildInputType(child.input_type))
  ) as T[]

  if (groupCode !== 'SHIPMENT' || secondaryChildNames.has('tags')) {
    return fromSameGroup
  }

  if (fromSameGroup.some((child) => child.name === 'tags')) {
    return fromSameGroup
  }

  const sourceOrderSetting = findSourcePlatformSettingByGroup(
    selectedPlatformSettingsByGroup,
    'ORDER'
  )
  const tagsChild = sourceOrderSetting?.children?.find((child) => child.name === 'tags')

  if (!tagsChild) {
    return fromSameGroup
  }

  return [...fromSameGroup, tagsChild as T]
}

export type SourceFileActionPanel = {
  platformId: number
  childSettings: SETTINGS_OF_PLATFORM[]
}

export const getConnectedSourceFileActionPanels = (
  groupCode: string,
  parentSettingName: string,
  secondaryChildren: SETTINGS_OF_PLATFORM[] | null | undefined,
  sources: Array<{
    catalog: SETTINGS_OF_PLATFORM[] | null | undefined
    platformId?: number | string | null
  }>
): SourceFileActionPanel[] => {
  const panels: SourceFileActionPanel[] = []
  const seenPlatformIds = new Set<number>()

  for (const source of sources) {
    const platformId = Number(source.platformId)
    if (!Number.isFinite(platformId) || platformId <= 0 || seenPlatformIds.has(platformId)) {
      continue
    }

    const fileChildren = getDirectSettingFileChildActions(
      getSourceAdditionalChildSettings(
        source.catalog,
        groupCode,
        parentSettingName,
        secondaryChildren
      )
    )

    if (!fileChildren.length) {
      continue
    }

    seenPlatformIds.add(platformId)
    panels.push({
      platformId,
      childSettings: fileChildren,
    })
  }

  return panels
}

export const findSourcePlatformSettingByGroup = <T extends SourcePlatformSetting>(
  settings: T[] | null | undefined,
  groupCode: string,
  parentSettingName?: string
): T | undefined => {
  if (!settings?.length || !groupCode) {
    return undefined
  }

  if (parentSettingName) {
    const byName = settings.find(
      (item) => item.group_code === groupCode && item.name === parentSettingName
    )
    if (byName) {
      return byName
    }
  }

  const withMappings = settings.find(
    (item) =>
      item.group_code === groupCode &&
      ((item.field_mappings?.length ?? 0) > 0 || (item.mappings_data?.length ?? 0) > 0)
  )
  if (withMappings) {
    return withMappings
  }

  return settings.find((item) => item.group_code === groupCode)
}

export const findSourcePlatformSettingInLists = <T extends SourcePlatformSetting>(
  lists: Array<T[] | null | undefined>,
  groupCode: string,
  parentSettingName?: string
): T | undefined => {
  for (const list of lists) {
    const found = findSourcePlatformSettingByGroup(list, groupCode, parentSettingName)
    if (found) {
      return found
    }
  }

  return undefined
}

type ChildMappingSourceSetting = {
  name?: string
  field_mappings?: unknown[] | null
  mappings_data?: unknown[] | null
  children?: ChildMappingSourceSetting[] | null
}

// Copy field_code from api_code when missing; in: field_mappings or mappings_data rows; out: rows usable as child field_mappings.
export const normalizeChildFieldMappings = (
  mappings: Array<Record<string, any>> | null | undefined
): Array<Record<string, any>> => {
  if (!Array.isArray(mappings)) {
    return []
  }

  return mappings
    .filter(Boolean)
    .map((mapping) => ({
      ...mapping,
      field_code: mapping.field_code || mapping.api_code,
    }))
    .filter((mapping) => Boolean(mapping.field_code))
}

// Prefer setting.field_mappings, else mappings_data; in: one setting object; out: normalized mapping rows.
const getSettingFieldMappings = (
  setting?: ChildMappingSourceSetting | null
): Array<Record<string, any>> => {
  const fromFieldMappings = normalizeChildFieldMappings(
    setting?.field_mappings as Array<Record<string, any>> | undefined
  )
  if (fromFieldMappings.length) {
    return fromFieldMappings
  }

  return normalizeChildFieldMappings(
    setting?.mappings_data as Array<Record<string, any>> | undefined
  )
}

export type ResolvedChildMappingSource = {
  fieldMappings: Array<Record<string, any>>
  fromSource: boolean
}

// Child mappings: own field_mappings, else same-name source child; in: dest child + source group; out: {fieldMappings, fromSource}. Parent mappings stay on parent.
export const resolveChildMappingSource = ({
  childSetting,
  sourceGroupSetting,
}: {
  childSetting?: ChildMappingSourceSetting | null
  sourceGroupSetting?: ChildMappingSourceSetting | null
}): ResolvedChildMappingSource => {
  const ownMappings = getSettingFieldMappings(childSetting)
  if (ownMappings.length) {
    return {fieldMappings: ownMappings, fromSource: false}
  }

  const childName = childSetting?.name ?? ''
  const exactSourceChild = (sourceGroupSetting?.children ?? []).find(
    (child) => child?.name === childName
  )
  const exactSourceMappings = getSettingFieldMappings(exactSourceChild)
  if (exactSourceMappings.length) {
    return {fieldMappings: exactSourceMappings, fromSource: true}
  }

  return {fieldMappings: [], fromSource: false}
}

export const normalizeMappingGroupCode = (groupCode?: string) => {
  if (!groupCode) {
    return ''
  }

  if (groupCode.endsWith('_setting')) {
    return groupCode.replace(/_setting$/, '').toUpperCase()
  }

  return groupCode
}

/** Schema group (`SHIPMENT`) and form container (`shipment_setting`) are the same section. */
export const mappingGroupMatchesFilter = (
  groupCode: string | undefined,
  filter: ReadonlySet<string>
): boolean => {
  const raw = `${groupCode ?? ''}`
  const normalized = normalizeMappingGroupCode(raw)
  if (filter.has(raw) || (normalized && filter.has(normalized))) return true

  for (const item of filter) {
    if (normalizeMappingGroupCode(item) === normalized) return true
  }

  return false
}

const hasNonEmptyMappingValue = (value: any) => {
  if (Array.isArray(value)) {
    return value.length > 0
  }

  return (
    value !== null &&
    value !== undefined &&
    !(typeof value === 'string' && value.trim() === '')
  )
}

export const hasMappingDatasetValue = (
  dataset: FieldMappingSaveEntry[] = [],
  mappingEntry?: FieldMappingSaveEntry
) =>
  dataset.some((item) => {
    if (item?.hasPair === false) {
      return false
    }

    const requiresPair =
      mappingEntry?.sourceFieldId !== null &&
      mappingEntry?.sourceFieldId !== undefined &&
      mappingEntry?.destinationFieldId !== null &&
      mappingEntry?.destinationFieldId !== undefined

    if (requiresPair) {
      return (
        hasNonEmptyMappingValue(item?.source_platform_data_id) &&
        hasNonEmptyMappingValue(item?.destination_platform_data_id)
      )
    }

    return (
      hasNonEmptyMappingValue(item?.destination_platform_data_id) ||
      hasNonEmptyMappingValue(item?.source_platform_data_id) ||
      hasNonEmptyMappingValue(item?.destination_platform_data_str) ||
      hasNonEmptyMappingValue(item?.source_platform_data_str)
    )
  })

type MappingGroupSettingContext = {
  id?: number | string | null
  type?: string
  mappings_data?: FieldMappingSaveEntry[]
}

export type RequiredMappingValidationContext = {
  mapping: FieldMappingSaveEntry
  owningGroupSetting?: MappingGroupSettingContext | null
  oppositeGroupSetting?: MappingGroupSettingContext | null
  matchingFieldMappings?: FieldMappingSaveEntry[]
  /** Full pending form mappings (all setting_mapping_id rows); used for one-to-one hide detection. */
  allPendingMappings?: FieldMappingSaveEntry[]
  /** Saved API field mappings; used for one-to-one hide detection. */
  allSavedMappings?: FieldMappingSaveEntry[]
  resolveMappingValue?: (settingMappingId: number | null | undefined) => string | null
}

/** Hide rules shared by channel-settings render and save validation. */
export const isMappingHiddenByChannelSettingsRenderRules = ({
  mapping,
  owningGroupSetting,
  oppositeGroupSetting,
  allPendingMappings = [],
  allSavedMappings = [],
}: Pick<
  RequiredMappingValidationContext,
  | 'mapping'
  | 'owningGroupSetting'
  | 'oppositeGroupSetting'
  | 'allPendingMappings'
  | 'allSavedMappings'
>): boolean => {
  const oppositeMappings = oppositeGroupSetting?.mappings_data ?? []
  const owningMappings = owningGroupSetting?.mappings_data ?? []

  return Boolean(
    shouldHideDefaultLocationMapping(
      mapping,
      owningGroupSetting?.type,
      oppositeMappings,
      owningMappings
    ) ||
      shouldHideDefaultLocationMapping(
        mapping,
        oppositeGroupSetting?.type,
        owningMappings,
        oppositeMappings
      ) ||
      shouldSourceLocationOwnPair(mapping, oppositeMappings, owningMappings) ||
      shouldHideDefaultWhenOneToOneExists({
        mapping,
        savedMappings: allSavedMappings,
        pendingMappings: allPendingMappings,
        settingId: owningGroupSetting?.id ?? null,
        targetCode: 'SHIPPINGPROVIDER',
      })
  )
}

type FieldMappingRequiredContext = {
  sourceSide?: FieldMappingSaveEntry | null
  destinationSide?: FieldMappingSaveEntry | null
  savedMapping?: FieldMappingSaveEntry | null
}

/** Align UI/save required state for independent and paired mapping rows. */
export const resolveFieldMappingIsRequired = (
  fieldMapping?: FieldMappingSaveEntry | null,
  options?: FieldMappingRequiredContext
): boolean => {
  if (options?.sourceSide || options?.destinationSide) {
    return Boolean(
      fieldMapping?.isRequired ||
        options?.sourceSide?.isRequired ||
        options?.destinationSide?.isRequired
    )
  }

  return Boolean(
    fieldMapping?.isRequired ||
      options?.savedMapping?.destination_field?.isRequired ||
      options?.savedMapping?.source_field?.isRequired
  )
}

export const findMappingDisplayNameInSettingsCatalog = (
  settingsCatalog: Array<{mappings_data?: FieldMappingSaveEntry[] | null}> = [],
  settingMappingId?: number | null,
  fieldCode?: string | null
): string => {
  if (settingMappingId !== undefined && settingMappingId !== null) {
    for (const setting of settingsCatalog) {
      const match = (setting?.mappings_data || []).find(
        (mapping) => mapping?.setting_mapping_id === settingMappingId
      )
      if (match?.display_name) {
        return `${match.display_name}`
      }
      if (match?.api_code) {
        return `${match.api_code}`
      }
    }
  }

  if (fieldCode) {
    return fieldCode
  }

  if (settingMappingId !== undefined && settingMappingId !== null) {
    return `Mapping ${settingMappingId}`
  }

  return 'Required mapping'
}

/**
 * Skip save-time required-mapping validation when the mapping is hidden by the same
 * rules used at render time, already has a pending/saved value, or cannot be edited
 * in the UI (e.g. paired DependentMapping rows that never mount).
 */
export const shouldSkipRequiredMappingValidation = ({
  mapping,
  owningGroupSetting,
  oppositeGroupSetting,
  matchingFieldMappings = [],
  allPendingMappings = [],
  allSavedMappings = [],
  resolveMappingValue,
}: RequiredMappingValidationContext): boolean => {
  const settingMappingId = mapping?.setting_mapping_id
  if (settingMappingId === undefined || settingMappingId === null) {
    return true
  }

  const hasPendingValue = matchingFieldMappings.some((item) =>
    hasMappingDatasetValue(item?.fieldMappingDataSet ?? [], item)
  )
  if (hasPendingValue) {
    return true
  }

  if (resolveMappingValue) {
    const existingValue = resolveMappingValue(settingMappingId)
    if (existingValue !== null && `${existingValue}`.trim() !== '') {
      return true
    }
  }

  if (
    isMappingHiddenByChannelSettingsRenderRules({
      mapping,
      owningGroupSetting,
      oppositeGroupSetting,
      allPendingMappings,
      allSavedMappings,
    })
  ) {
    return true
  }

  if (!mapping?.isDefault && matchingFieldMappings.length === 0) {
    return true
  }

  return false
}

export const isFieldMappingSaveable = (
  entry: FieldMappingSaveEntry,
  mappingForSave: FieldMappingSaveEntry[] = []
): boolean => {
  const destinationFieldId = entry?.destinationFieldId ?? null

  if (destinationFieldId !== null && destinationFieldId !== undefined) {
    return true
  }

  const hasExistingMappingId =
    entry?.field_mapping_id != null ||
    entry?.savedMappingId != null

  if (hasExistingMappingId) {
    return hasMappingDatasetValue(mappingForSave, entry)
  }

  return false
}

export const mapDependentRowsToFieldMappingDataSet = (
  rows: FieldMappingSaveEntry[] = [],
  isDefault?: boolean
): ShippingMethodMapping[] =>
  rows.map(({source_platform_data, destination_platform_data, ...rest}) => {
    const sourceId = source_platform_data?.id ?? rest.source_platform_data_id ?? null
    const destinationId =
      destination_platform_data?.id ?? rest.destination_platform_data_id ?? null

    return {
      ...rest,
      id: rest.id ?? null,
      source_platform_data_id: sourceId,
      source_platform_data_str:
        sourceId !== null && sourceId !== undefined ? String(sourceId) : rest.source_platform_data_str ?? '',
      destination_platform_data_id: destinationId,
      destination_platform_data_str:
        destinationId !== null && destinationId !== undefined
          ? String(destinationId)
          : rest.destination_platform_data_str ?? '',
      hasPair:
        rest.hasPair ??
        Boolean(
          hasNonEmptyMappingValue(sourceId) && hasNonEmptyMappingValue(destinationId)
        ),
      isDefault,
    }
  })

type BuildFieldMappingsSaveRequestArgs = {
  createResponseData: Record<string, any> | null | undefined
  fieldMappings: FieldMappingSaveEntry[]
  savedMappings: FieldMappingSaveEntry[]
  platformId: number | string
  includeGroupCodes?: ReadonlySet<string> | null
  excludeGroupCodes?: ReadonlySet<string> | null
}

const resolveMappingIdForQuery = (
  mappingField: FieldMappingSaveEntry,
  savedMappings: FieldMappingSaveEntry[] = []
) => {
  const directId = mappingField?.savedMappingId ?? mappingField?.field_mapping_id
  if (directId !== null && directId !== undefined) {
    return directId
  }

  const fallbackFromSavedMappings = (savedMappings || []).find((savedItem: any) => {
    if (!savedItem) return false

    const destinationMatch =
      mappingField?.destinationFieldId !== null &&
      mappingField?.destinationFieldId !== undefined &&
      savedItem?.destination_field?.id === mappingField?.destinationFieldId

    const sourceMatch =
      mappingField?.sourceFieldId === null || mappingField?.sourceFieldId === undefined
        ? savedItem?.source_field === null
        : savedItem?.source_field?.id === mappingField?.sourceFieldId

    const defaultMatch =
      mappingField?.isDefault === undefined || savedItem?.isDefault === mappingField?.isDefault

    const groupSettingMatch =
      mappingField?.savedSettingId === null ||
      mappingField?.savedSettingId === undefined ||
      savedItem?.setting_id === mappingField?.savedSettingId

    if (destinationMatch && sourceMatch && defaultMatch && groupSettingMatch) {
      return true
    }

    const hasPairIdentity =
      mappingField?.destinationFieldId !== null &&
      mappingField?.destinationFieldId !== undefined

    return (
      !hasPairIdentity &&
      mappingField?.setting_mapping_id !== null &&
      mappingField?.setting_mapping_id !== undefined &&
      savedItem?.setting_mapping_id === mappingField?.setting_mapping_id
    )
  })

  return fallbackFromSavedMappings?.id ?? null
}

const hasAnyMappingValue = (settingField: FieldMappingSaveEntry, item: FieldMappingSaveEntry) => {
  const hasPersistedMappingDataId = item?.id !== null && item?.id !== undefined
  const hasDest =
    (item.destination_platform_data_id !== null &&
      item.destination_platform_data_id !== undefined &&
      item.destination_platform_data_id !== '') ||
    (item.destination_platform_data_str ?? '') !== ''
  const hasSource =
    (item.source_platform_data_id !== null &&
      item.source_platform_data_id !== undefined &&
      item.source_platform_data_id !== '') ||
    (item.source_platform_data_str ?? '') !== ''
  const requiresSourcePair =
    !settingField?.isDefault &&
    settingField?.sourceFieldId !== null &&
    settingField?.sourceFieldId !== undefined
  const hasPair = item?.hasPair !== false

  return {
    hasPersistedMappingDataId,
    hasDest,
    hasSource,
    hasPair,
    hasValue: requiresSourcePair ? hasDest && hasSource : hasDest,
  }
}

export const buildFieldMappingsSaveRequest = ({
  createResponseData,
  fieldMappings,
  savedMappings,
  platformId,
  includeGroupCodes = null,
  excludeGroupCodes = null,
}: BuildFieldMappingsSaveRequestArgs): {payload: FieldMappingSaveEntry[]; field_mapping_ids: any[]} | null => {
  if (!createResponseData || !fieldMappings.length) return null

  const payload: FieldMappingSaveEntry[] = []
  const platformMappingIdSet = new Set<any>()
  const normalizedFieldMappings = dedupeFieldMappingsForSave(fieldMappings, savedMappings)

  for (const settingField of normalizedFieldMappings) {
    const groupCode = `${settingField.groupCode ?? ''}`
    if (includeGroupCodes && !mappingGroupMatchesFilter(groupCode, includeGroupCodes)) continue
    if (excludeGroupCodes && mappingGroupMatchesFilter(groupCode, excludeGroupCodes)) continue
    if (!createResponseData[groupCode]) continue

    const mappingDataset = settingField.fieldMappingDataSet || []
    const hadPersistedMappingEntries = mappingDataset.some(
      (item) => item?.id !== null && item?.id !== undefined
    )

    const clearedPersistedRows = mappingDataset.filter((item) => {
      const {hasPersistedMappingDataId, hasValue} = hasAnyMappingValue(settingField, item)
      return hasPersistedMappingDataId && !hasValue
    })

    const clearedPersistedRowIds = new Set(clearedPersistedRows.map((item) => item.id))

    const sanitizedDataset = mappingDataset.filter((item) => {
      if (item?.id !== null && item?.id !== undefined && clearedPersistedRowIds.has(item.id)) {
        return false
      }
      const {hasPersistedMappingDataId, hasPair, hasValue} = hasAnyMappingValue(settingField, item)
      return (hasPersistedMappingDataId && hasValue) || (hasPair && hasValue)
    })

    let mapping_for_save = sanitizedDataset.map((item) => ({
      ...(item?.id ? {id: item.id} : {}),
      source_platform_data_id:
        item?.source_platform_data_id !== undefined ? item.source_platform_data_id : null,
      destination_platform_data_id:
        item?.destination_platform_data_id !== null &&
        item?.destination_platform_data_id !== undefined &&
        item?.destination_platform_data_id !== '' &&
        /^[+-]?\d+$/.test(item.destination_platform_data_id)
          ? Number(item.destination_platform_data_id)
          : item?.destination_platform_data_id ?? null,
      source_platform_data_str: item?.source_platform_data_str ?? '',
      destination_platform_data_str: item?.destination_platform_data_str ?? '',
      isDefault: settingField?.isDefault,
    }))

    const hasExistingMappingId =
      settingField?.savedMappingId !== null && settingField?.savedMappingId !== undefined
        ? true
        : settingField?.field_mapping_id !== null && settingField?.field_mapping_id !== undefined

    if (mapping_for_save.length === 0 && !hadPersistedMappingEntries && !hasExistingMappingId) {
      mapping_for_save = [
        {
          source_platform_data_id: null,
          destination_platform_data_id: null,
          source_platform_data_str: '',
          destination_platform_data_str: '',
          isDefault: settingField?.isDefault,
        },
      ]
    }

    const mappingIdForQuery = resolveMappingIdForQuery(settingField, savedMappings)
    if (mappingIdForQuery !== null && mappingIdForQuery !== undefined) {
      platformMappingIdSet.add(mappingIdForQuery)
    }

    if (!isFieldMappingSaveable(settingField, mapping_for_save)) {
      continue
    }

    payload.push({
      [groupCode + '_id']: createResponseData[groupCode]['id'],
      field_mapping_id: settingField.field_mapping_id ?? mappingIdForQuery ?? null,
      isDefault: settingField?.isDefault,
      setting_mapping_id: resolveSettingMappingIdForSave(settingField, savedMappings),
      source_field_id: settingField.sourceFieldId,
      destination_field_id: settingField.destinationFieldId,
      fieldmappingdata_set: mapping_for_save || [],
    })
  }

  if (!payload.length) return null

  return {
    payload,
    field_mapping_ids: Array.from(platformMappingIdSet),
  }
}
